/**
 * Oji Newsletter Popup — paired with sections/oji-newsletter-popup.liquid.
 * Vanilla JS, no dependencies. Handles: session/local suppression, delay +
 * exit-intent triggers, open/close + focus trap, progressive-enhancement
 * form submission, and the copy-to-clipboard discount code control.
 */
(function () {
  'use strict';

  const root = document.querySelector('[data-oji-nlpop]');
  if (!root) return;

  /* Extra runtime guard for checkout-like contexts. The Liquid layer already
     excludes the cart page/template, but this keeps the component inert if
     it ever ends up rendered somewhere a checkout class is present. */
  if (/\bcheckout\b/i.test(document.body.className)) return;

  const SESSION_KEY = 'ojiNlpopShown';
  const LOCAL_KEY = 'ojiNlpopUntil';
  /* Set right before this popup's own native form POST; read after the reload
     to reopen the popup in success state. Scopes success to THIS form only. */
  const SUBMIT_FLAG = 'ojiNlpopJustSubmitted';
  /* Permanent (never expires): once a visitor subscribes, never show the popup
     to them again on this device — separate from the timed reappear window that
     applies to people who merely dismissed it. */
  const SUBSCRIBED_KEY = 'ojiNlpopSubscribed';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const overlay = root.querySelector('[data-oji-nlpop-overlay]');
  const dialog = root.querySelector('[data-oji-nlpop-dialog]');
  const closeBtn = root.querySelector('[data-oji-nlpop-close]');
  const formState = root.querySelector('[data-oji-nlpop-form-state]');
  const successState = root.querySelector('[data-oji-nlpop-success]');
  const copyBtn = root.querySelector('[data-oji-nlpop-copy]');
  const form = root.querySelector('.oji-nlpop__form');
  const jsError = root.querySelector('[data-oji-nlpop-js-error]');

  let delaySeconds = parseInt(root.getAttribute('data-delay'), 10);
  if (!delaySeconds || delaySeconds < 0) delaySeconds = 15;
  let reappearDays = parseInt(root.getAttribute('data-reappear-days'), 10);
  if (!reappearDays || reappearDays < 0) reappearDays = 14;

  const prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let lastFocusedEl = null;
  let triggered = false;
  let triggerTimerId = null;

  /* -------------------------------------------------------------------- *
   * Storage helpers — session = "already shown this browser session",
   * local = "suppressed until this timestamp" (set on close/subscribe).
   * -------------------------------------------------------------------- */
  function isSuppressed() {
    try {
      if (localStorage.getItem(SUBSCRIBED_KEY) === '1') return true;
    } catch (err) {
      /* storage unavailable (private mode, etc.) — fall through */
    }
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return true;
    } catch (err) {
      /* noop */
    }
    try {
      const until = parseInt(localStorage.getItem(LOCAL_KEY), 10);
      if (until && Date.now() < until) return true;
    } catch (err) {
      /* noop */
    }
    return false;
  }

  function markSubscribed() {
    try {
      localStorage.setItem(SUBSCRIBED_KEY, '1');
    } catch (err) {
      /* noop */
    }
  }

  function markShownThisSession() {
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch (err) {
      /* noop */
    }
  }

  function suppressForReappearWindow() {
    try {
      const until = Date.now() + reappearDays * 24 * 60 * 60 * 1000;
      localStorage.setItem(LOCAL_KEY, String(until));
    } catch (err) {
      /* noop */
    }
  }

  /* -------------------------------------------------------------------- *
   * Focus trap
   * -------------------------------------------------------------------- */
  function getFocusableEls() {
    if (!dialog) return [];
    const selector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const candidates = Array.prototype.slice.call(dialog.querySelectorAll(selector));
    return candidates.filter(function (el) {
      return el.offsetParent !== null;
    });
  }

  function handleFocusTrap(event) {
    if (event.key !== 'Tab') return;
    const focusable = getFocusableEls();
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function handleKeydown(event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      closePopup();
      return;
    }
    handleFocusTrap(event);
  }

  /* -------------------------------------------------------------------- *
   * Open / close
   * -------------------------------------------------------------------- */
  function openPopup() {
    if (root.classList.contains('is-open')) return;
    lastFocusedEl = document.activeElement;
    root.classList.add('is-open');
    document.documentElement.classList.add('oji-nlpop-lock');
    markShownThisSession();
    document.addEventListener('keydown', handleKeydown, true);
    window.setTimeout(
      function () {
        if (closeBtn) closeBtn.focus();
      },
      prefersReducedMotion ? 0 : 60
    );
  }

  function closePopup() {
    if (!root.classList.contains('is-open')) return;
    root.classList.remove('is-open');
    document.documentElement.classList.remove('oji-nlpop-lock');
    document.removeEventListener('keydown', handleKeydown, true);
    suppressForReappearWindow();
    if (lastFocusedEl && typeof lastFocusedEl.focus === 'function') {
      lastFocusedEl.focus();
    }
  }

  if (closeBtn) {
    closeBtn.addEventListener('click', closePopup);
  }
  if (overlay) {
    overlay.addEventListener('click', closePopup);
  }

  /* -------------------------------------------------------------------- *
   * Success state reveal
   * -------------------------------------------------------------------- */
  function showSuccessState() {
    /* Confirmed subscribe → never show the popup to this visitor again. */
    markSubscribed();
    root.classList.add('is-success');
    if (formState) formState.hidden = true;
    if (successState) successState.hidden = false;
    if (dialog && successState) {
      const successHeading = successState.querySelector('.oji-nlpop__heading');
      if (successHeading && successHeading.id) {
        dialog.setAttribute('aria-labelledby', successHeading.id);
      }
    }
  }

  /* -------------------------------------------------------------------- *
   * Copy-to-clipboard
   * -------------------------------------------------------------------- */
  if (copyBtn) {
    copyBtn.addEventListener('click', function () {
      const code = copyBtn.getAttribute('data-code') || '';
      const originalLabel = copyBtn.textContent;
      const copiedLabel = copyBtn.getAttribute('data-copied-label') || originalLabel;

      function confirmCopied() {
        copyBtn.textContent = copiedLabel;
        copyBtn.classList.add('is-copied');
        window.setTimeout(function () {
          copyBtn.textContent = originalLabel;
          copyBtn.classList.remove('is-copied');
        }, 2000);
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(confirmCopied, function () {
          /* clipboard write rejected — leave the label untouched */
        });
        return;
      }

      /* Fallback for browsers without the async Clipboard API. */
      try {
        const tempInput = document.createElement('textarea');
        tempInput.value = code;
        tempInput.setAttribute('readonly', '');
        tempInput.style.position = 'fixed';
        tempInput.style.top = '-1000px';
        tempInput.style.opacity = '0';
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
        confirmCopied();
      } catch (err) {
        /* noop — copy simply doesn't happen */
      }
    });
  }

  /* -------------------------------------------------------------------- *
   * Form submission — native POST on purpose. Shopify's forms require an
   * invisible CAPTCHA token that only the native submit path produces; an
   * AJAX submit is rejected ("Missing CAPTCHA token"). We validate the email
   * format client-side and show a brand-styled inline message (never the
   * browser's generic bubble), then let the native submission proceed and
   * flag it so the post-reload handoff reopens the popup in its success state.
   * -------------------------------------------------------------------- */
  function showFieldError(message) {
    if (!jsError) return;
    jsError.textContent = message;
    jsError.hidden = false;
  }

  if (form) {
    const emailInput = form.querySelector('input[type="email"]');
    /* Disable the native validation bubble so our own styled message wins. */
    form.setAttribute('novalidate', 'novalidate');

    form.addEventListener('submit', function (event) {
      const value = emailInput ? emailInput.value.trim() : '';
      if (!EMAIL_RE.test(value)) {
        event.preventDefault();
        showFieldError('Ingresa un correo válido, por ejemplo nombre@correo.com.');
        if (emailInput) emailInput.focus();
        return;
      }
      /* Valid format → allow the native POST (CAPTCHA runs) and record that it
         was THIS popup submitting, so the reload can reopen success state. */
      if (jsError) jsError.hidden = true;
      try {
        /* Timestamp (not a bare flag): the handoff only honors it for ~60s, so a
           stale flag from an abandoned submit can't later hijack an unrelated
           customer-form reload and leak the code. */
        sessionStorage.setItem(SUBMIT_FLAG, String(Date.now()));
      } catch (err) {
        /* noop — worst case the success state just doesn't auto-open */
      }
      /* deliberately no preventDefault: the browser performs the native submit */
    });
  }

  /* -------------------------------------------------------------------- *
   * Success-after-native-submit handoff. After the popup form posts natively
   * the page reloads; if the submit-flag is set (only THIS popup sets it) we
   * reopen the popup. `[data-oji-nlpop-posted]` is rendered by Liquid when
   * form.posted_successfully? is true — combining the two means a footer/other
   * customer-form subscribe never reopens this popup.
   * -------------------------------------------------------------------- */
  let justSubmitted = false;
  try {
    const submittedAt = parseInt(sessionStorage.getItem(SUBMIT_FLAG), 10);
    justSubmitted = submittedAt > 0 && Date.now() - submittedAt < 60000;
  } catch (err) {
    /* noop */
  }
  if (justSubmitted) {
    try {
      sessionStorage.removeItem(SUBMIT_FLAG);
    } catch (err) {
      /* noop */
    }
    const posted = !!root.querySelector('[data-oji-nlpop-posted]');
    const serverError = root.querySelector('.oji-nlpop__error:not([data-oji-nlpop-js-error])');
    if (posted) {
      openPopup();
      showSuccessState();
      return;
    }
    if (serverError) {
      /* Server rejected a format-valid email (e.g. already registered) — reopen
         so the visitor sees Shopify's own error rather than nothing. */
      openPopup();
      return;
    }
  }

  /* -------------------------------------------------------------------- *
   * Trigger scheduling — delay timer or exit intent, whichever fires first.
   * If the popup was already seen this session (or is within its reappear
   * suppression window), it is never built/shown at all.
   * -------------------------------------------------------------------- */
  if (isSuppressed()) return;

  function fireTrigger() {
    if (triggered) return;
    triggered = true;
    if (triggerTimerId) window.clearTimeout(triggerTimerId);
    document.removeEventListener('mouseleave', handleExitIntent);
    /* Se relee la supresion al disparar, no solo al programar. El bloque de
       captura del articulo marca "ya suscrito" despues de que este temporizador
       quedara armado; sin esta segunda lectura el popup interrumpia a quien
       acababa de dejar su correo unos segundos antes, y encima con un descuento
       que ese bloque no ofrece. */
    if (isSuppressed()) return;
    openPopup();
  }

  function handleExitIntent(event) {
    if (event.clientY <= 0) fireTrigger();
  }

  triggerTimerId = window.setTimeout(fireTrigger, delaySeconds * 1000);

  const hasFinePointer = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
  if (hasFinePointer) {
    document.addEventListener('mouseleave', handleExitIntent);
  }
})();
